import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2 } from 'lucide-react'
import * as Mutation from '../../../api/gql/mutation'
import { MODULE_META } from '../../project-info/members/lib/moduleMeta'
import { ACTIONS, buildRolePermissions, keyOf } from './role-matrix-payload'

export interface RoleForEditor {
  id: string
  key: string | null
  name: string
  isSystem: boolean
  memberCount: number
  permissions: { module: string; actions: string[] }[]
}

interface Props {
  role: RoleForEditor
  onSaved: () => Promise<any>
}

const RoleMatrixEditor: React.FC<Props> = ({ role, onSaved }) => {
  const { t } = useTranslation()
  const initial = useMemo(() => {
    const s = new Set<string>()
    for (const p of role.permissions) for (const a of p.actions) s.add(keyOf(p.module, a))
    return s
  }, [role])
  const [draft, setDraft] = useState<Set<string>>(new Set(initial))
  const [save, { loading: saving }] = useMutation(Mutation.SET_ROLE_PERMISSIONS)

  const dirty = draft.size !== initial.size || [...draft].some((k) => !initial.has(k))
  // Any capability implies VIEW — the sidebar keys module visibility off VIEW, so
  // ADMIN-without-VIEW hides the module while looking granted (Freelo #31253329).
  // The server normalizes on save too; this keeps the checkboxes honest live.
  const toggle = (m: string, a: string) =>
    setDraft((prev) => {
      const k = keyOf(m, a)
      const n = new Set(prev)
      n.has(k) ? n.delete(k) : n.add(k)
      if (a !== 'VIEW' && ACTIONS.some((x) => x !== 'VIEW' && n.has(keyOf(m, x)))) n.add(keyOf(m, 'VIEW'))
      return n
    })
  const viewLocked = (m: string) => ACTIONS.some((a) => a !== 'VIEW' && draft.has(keyOf(m, a)))

  const handleSave = async () => {
    const permissions = buildRolePermissions(draft)
    try {
      await save({ variables: { roleId: role.id, permissions } })
      await onSaved()
      toastr.success(t('success', 'Success'), t('Roles_matrix_saved', 'Permissions saved'))
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{role.key ? t('role_' + role.key, role.name) : role.name}</h3>
        {role.isSystem && <Badge variant="secondary">{t('Roles_system_badge', 'System')}</Badge>}
        <div className="ml-auto">
          <Button size="sm" className="h-8" disabled={!dirty || saving} onClick={handleSave}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('save', 'Save')}
          </Button>
        </div>
      </div>
      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Roles_col_module', 'Module')}</TableHead>
              {ACTIONS.map((a) => (
                <TableHead key={a} className="text-center whitespace-nowrap">{t('perm_action_' + a, a)}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {MODULE_META.map(({ key: m, labelKey }) => (
              <TableRow key={m}>
                <TableCell className="font-medium whitespace-nowrap">{t(labelKey, m)}</TableCell>
                {ACTIONS.map((a) => (
                  <TableCell key={a} className="text-center">
                    <Checkbox
                      // VIEW renders as checked whenever any other action is on —
                      // that is what the server stores — even for legacy rows
                      // saved without it.
                      checked={draft.has(keyOf(m, a)) || (a === 'VIEW' && viewLocked(m))}
                      disabled={a === 'VIEW' && viewLocked(m)}
                      onCheckedChange={() => toggle(m, a)}
                      aria-label={`${m} ${a}`}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default RoleMatrixEditor
